import { emptyUser, ISocials } from '../providers/User/useUser';

const _convertHexToRgb = (color: string) => [
	parseInt(color.slice(1, 3), 16),
	parseInt(color.slice(3, 5), 16),
	parseInt(color.slice(5, 7), 16),
];

const _getLuminance = (r: number, g: number, b: number) => {
	const a = [r, g, b].map(v => {
		v /= 255;
		return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
	});
	return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
};

export const STRING_PLACEHOLDER = '…';

/**
 * Returns values from range 1-21; the greater, the better.
 */
export const contrastColors = (color1: number[] | string, color2: number[] | string) => {
	const rgb1 = typeof color1 === 'string' ? _convertHexToRgb(color1) : color1;
	const rgb2 = typeof color2 === 'string' ? _convertHexToRgb(color2) : color2;

	const lum1 = _getLuminance(rgb1[0], rgb1[1], rgb1[2]);
	const lum2 = _getLuminance(rgb2[0], rgb2[1], rgb2[2]);
	const brightest = Math.max(lum1, lum2);
	const darkest = Math.min(lum1, lum2);
	return (brightest + 0.05) / (darkest + 0.05);
};

export const trimAddress = (address: string) => `${address.slice(0, 6)}…${address.slice(34, 38)}`;
export const trimTransactionId = (id: string) => `${id.slice(0, 6)}…${id.slice(63, 67)}`;

export const trimString = (ifLongerThan: number, target: string) => {
	return target.length > ifLongerThan ? `${target.substring(0, ifLongerThan)}…` : target;
};

const formatFixedDecimals = (amount: string | number, decimals: number) => Number(amount).toFixed(decimals);
export const formatUsd = (amount: string | number) => `$${formatFixedDecimals(amount, 2)}`;
export const formatCartridge = (amount: string | number) => formatFixedDecimals(amount, 2);

export const parseSocials = (socialsRaw: string): ISocials => {
	if (!socialsRaw || socialsRaw === 'null') {
		return emptyUser.socials;
	}

	/** Server-held values can be malformed if not set via HashUp end-user interface */
	try {
		return JSON.parse(socialsRaw);
	} catch (e) {
		return emptyUser.socials;
	}
};

export const convertSnakeToCamelCase = (token: string) =>
	token.toLowerCase().replace(/(_\w)/g, group => group.toUpperCase().replace('_', ''));

export const isLinkExternal = (uri: string) => uri.startsWith('http');

export const capitalizeFirstLetter = (content: string) => `${content.charAt(0).toUpperCase()}${content.slice(1)}`;

/**
 * value                decimal places
 * [0, 0.0009]          full
 * [0.001, 0.999]       2
 * [1.00, 999.99]       2
 * [1000.0, 99999.9]    1
 * [100000, +inf]       0
 * @param balance input string
 */
export const trimBalance = (balance: string) => {
	const numericApproximation = parseFloat(balance);

	const decimalSeparatorIndex = balance.indexOf('.');

	if (numericApproximation >= 0 && numericApproximation < 0.001) {
		return balance;
	}
	if (numericApproximation >= 0.001 && numericApproximation < 1) {
		return balance.substring(0, decimalSeparatorIndex + 3);
	}
	if (numericApproximation >= 1 && numericApproximation < 1000) {
		return balance.substring(0, decimalSeparatorIndex + 3);
	}
	if (numericApproximation >= 1000 && numericApproximation < 100000) {
		return balance.substring(0, decimalSeparatorIndex + 2);
	}
	if (numericApproximation >= 100000) {
		return balance.substring(0, decimalSeparatorIndex);
	}

	return balance;
};

export const buildIpfsGateway = <T = any>(ipfs: T | string) =>
	typeof ipfs === 'string' ? (ipfs.match(/^ipfs:\/\//gm) ? `https://ipfs.io/ipfs/${ipfs.slice(7)}` : ipfs) : ipfs;
